import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon
} from '@mui/material';
import {
  Edit as EditIcon,
  Check as CheckIcon,
  Block as BlockIcon,
  Refresh as RefreshIcon,
  AdminPanelSettings as AdminIcon,
  LockOpen as LockOpenIcon,
  MoreVert as MoreVertIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { userApi } from '../api/auth';
import { User, UserStatus, UserRole, UserAdminUpdate } from '../types/user';
import ProtectedRoute from '../components/ProtectedRoute';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../api/auth';

const UsersPage: React.FC = () => {
  return (
    <ProtectedRoute adminOnly>
      <UserManagement />
    </ProtectedRoute>
  );
};

const UserManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [ecwidId, setEcwidId] = useState<string>('');
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    userId: number | null;
    userName: string;
    action: 'approve' | 'block' | 'unblock' | 'makeAdmin' | 'edit' | null;
  }>({
    open: false,
    userId: null,
    userName: '',
    action: null
  });
  
  // State for the "More Actions" menu
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [currentUserForMenu, setCurrentUserForMenu] = useState<User | null>(null);
  const isMoreMenuOpen = Boolean(moreMenuAnchorEl);

  // New state for reset password dialog
  const [resetPasswordDialog, setResetPasswordDialog] = useState<{
    open: boolean;
    userId: number | null;
    userName: string;
  }>({
    open: false,
    userId: null,
    userName: ''
  });
  const [tempPassword, setTempPassword] = useState('');
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(null);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState<string | null>(null);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);

  // Handlers for "More Actions" menu
  const handleMoreMenuOpen = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setMoreMenuAnchorEl(event.currentTarget);
    setCurrentUserForMenu(user);
  };

  const handleMoreMenuClose = () => {
    setMoreMenuAnchorEl(null);
    setCurrentUserForMenu(null);
  };

  const handleMoreMenuAction = (action: 'edit' | 'makeAdmin' | 'loginAs' | 'resetPassword') => {
    if (!currentUserForMenu) return;
    if (action === 'edit') {
      handleEditUser(currentUserForMenu);
    } else if (action === 'makeAdmin') {
      handleMakeAdmin(currentUserForMenu);
    } else if (action === 'loginAs') {
      handleLoginAsUser(currentUserForMenu);
    } else if (action === 'resetPassword') {
      handleResetPassword(currentUserForMenu);
    }
    handleMoreMenuClose();
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await userApi.getUsers(
          page * rowsPerPage,
          rowsPerPage,
          undefined,
          undefined,
          searchTerm || undefined
        );
        setUsers(data);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to fetch users');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [page, rowsPerPage, searchTerm, refresh]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleApproveUser = (user: User) => {
    setEcwidId(user.ecwid_customer_id?.toString() || '');
    setActionDialog({
      open: true,
      userId: user.id,
      userName: `${user.first_name} ${user.last_name}`,
      action: 'approve'
    });
  };

  const handleBlockUser = (user: User) => {
    setActionDialog({
      open: true,
      userId: user.id,
      userName: `${user.first_name} ${user.last_name}`,
      action: 'block'
    });
  };

  const handleUnblockUser = (user: User) => {
    setActionDialog({
      open: true,
      userId: user.id,
      userName: `${user.first_name} ${user.last_name}`,
      action: 'unblock'
    });
  };

  const handleMakeAdmin = (user: User) => {
    setActionDialog({
      open: true,
      userId: user.id,
      userName: `${user.first_name} ${user.last_name}`,
      action: 'makeAdmin'
    });
  };

  const handleEditUser = (user: User) => {
    setEcwidId(user.ecwid_customer_id?.toString() || '');
    setActionDialog({
      open: true,
      userId: user.id,
      userName: `${user.first_name} ${user.last_name}`,
      action: 'edit'
    });
  };

  const handleLoginAsUser = async (user: User) => {
    try {
      setLoading(true);
      setError(null);
      await authApi.loginAsUser(user.id);
      
      // Redirect to homepage after successful login
      window.location.href = '/';
    } catch (err: any) {
      setError(err.response?.data?.detail || `Failed to login as ${user.first_name} ${user.last_name}`);
      setLoading(false);
    }
  };

  const handleResetPassword = (user: User) => {
    setResetPasswordDialog({
      open: true,
      userId: user.id,
      userName: `${user.first_name} ${user.last_name}`
    });
    setTempPassword('');
    setResetPasswordError(null);
    setResetPasswordSuccess(null);
  };

  const handleResetPasswordClose = () => {
    setResetPasswordDialog({
      open: false,
      userId: null,
      userName: ''
    });
  };

  const handleResetPasswordSubmit = async () => {
    if (!resetPasswordDialog.userId) return;

    // Validate password
    if (tempPassword.length < 8) {
      setResetPasswordError('Password must be at least 8 characters long');
      return;
    }

    setResetPasswordLoading(true);
    setResetPasswordError(null);

    try {
      await userApi.resetPassword(resetPasswordDialog.userId, tempPassword);
      setResetPasswordSuccess(`Password has been reset for ${resetPasswordDialog.userName}`);
      
      // Clear form and close dialog after a short delay
      setTimeout(() => {
        setResetPasswordDialog({
          open: false,
          userId: null,
          userName: ''
        });
        setResetPasswordSuccess(null);
      }, 2000);
    } catch (err: any) {
      setResetPasswordError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handleConfirmAction = async () => {
    if (!actionDialog.userId || !actionDialog.action) return;

    setLoading(true);
    setError(null);
    setSuccess(null);
    const currentAction = actionDialog.action;
    const currentUserName = actionDialog.userName;
    const currentUserId = actionDialog.userId;

    try {
      let successMessage = '';
      if (currentAction === 'approve') {
        if (ecwidId) {
          await userApi.adminUpdateUser(currentUserId, { ecwid_customer_id: parseInt(ecwidId) });
        }
        await userApi.approveUser(currentUserId);
        successMessage = `User ${currentUserName} has been approved` + 
          (ecwidId ? ' and linked to Ecwid ID ' + ecwidId : '');
      } else if (currentAction === 'block') {
        await userApi.blockUser(currentUserId);
        successMessage = `User ${currentUserName} has been blocked`;
      } else if (currentAction === 'unblock') {
        await userApi.unblockUser(currentUserId);
        successMessage = `User ${currentUserName} has been unblocked`;
      } else if (currentAction === 'makeAdmin') {
        await userApi.adminUpdateUser(currentUserId, { role: UserRole.ADMIN });
        successMessage = `User ${currentUserName} has been made an admin`;
      } else if (currentAction === 'edit') {
        const parsedId = parseInt(ecwidId);
        let updateData: UserAdminUpdate;

        if (ecwidId === '') {
          updateData = { ecwid_customer_id: undefined };
          await userApi.adminUpdateUser(currentUserId, updateData);
          successMessage = `Cleared Ecwid ID for ${currentUserName}`;
        } else if (!isNaN(parsedId)) {
          updateData = { ecwid_customer_id: parsedId };
          await userApi.adminUpdateUser(currentUserId, updateData);
          successMessage = `Updated Ecwid ID for ${currentUserName} to ${parsedId}`;
        } else {
          throw new Error("Invalid Ecwid ID entered. Please enter a number or leave blank to clear.");
        }
      }

      if (successMessage) {
         setSuccess(successMessage);
      }

      // Close dialog and refresh user list
      setActionDialog({ open: false, userId: null, userName: '', action: null });
      setEcwidId('');
      setRefresh(prev => prev + 1);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
       const errorDetail = err.response?.data?.detail || err.message || `Failed to ${currentAction} user ${currentUserName}`;
       setError(errorDetail);
       // Keep dialog open on error
       setActionDialog(prev => ({ ...prev, open: true })); 
    } finally {
      // Only set loading false if dialog didn't stay open
      if (!actionDialog.open || currentAction !== 'edit') {
         setLoading(false); 
      }
    }
  };

  const handleCancelAction = () => {
    setActionDialog({
      open: false,
      userId: null,
      userName: '',
      action: null
    });
    setEcwidId('');
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case UserStatus.APPROVED:
        return <Chip label="Approved" color="success" size="small" />;
      case UserStatus.PENDING:
        return <Chip label="Pending" color="warning" size="small" />;
      case UserStatus.BLOCKED:
        return <Chip label="Blocked" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          User Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View and manage user accounts
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <TextField
            label="Search users"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ width: 300 }}
          />
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => setRefresh(prev => prev + 1)}
            variant="outlined"
          >
            Refresh
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Ecwid ID</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>{user.id}</TableCell>
                    <TableCell>
                      {user.first_name} {user.last_name}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.role === UserRole.ADMIN ? 'Admin' : 'Client'}
                        color={user.role === UserRole.ADMIN ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{getStatusChip(user.status)}</TableCell>
                    <TableCell>
                      {user.ecwid_customer_id ?? <em>Not linked</em>}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {user.status === UserStatus.PENDING && (
                        <Button
                          variant="text"
                          color="success"
                          size="small"
                          startIcon={<CheckIcon />}
                          onClick={() => handleApproveUser(user)}
                          sx={{ mr: 1 }}
                        >
                          Approve
                        </Button>
                      )}
                      {user.status === UserStatus.APPROVED && (
                         <Button
                           variant="text"
                           color="error"
                           size="small"
                           startIcon={<BlockIcon />}
                           onClick={() => handleBlockUser(user)}
                           disabled={user.id === currentUser?.id}
                           title={user.id === currentUser?.id ? "Cannot block yourself" : "Block user"}
                           sx={{ mr: 1 }}
                         >
                           Block
                         </Button>
                       )}
                      {user.status === UserStatus.BLOCKED && (
                        <Button
                          variant="text"
                          color="warning"
                          size="small"
                          startIcon={<LockOpenIcon />}
                          onClick={() => handleUnblockUser(user)}
                          sx={{ mr: 1 }}
                        >
                          Unblock
                        </Button>
                      )}

                      {((user.status === UserStatus.APPROVED || user.status === UserStatus.BLOCKED) && user.id !== currentUser?.id || 
                       (user.role !== UserRole.ADMIN && user.status === UserStatus.APPROVED)) && (
                        <IconButton
                          aria-label="more actions"
                          size="small"
                          onClick={(e) => handleMoreMenuOpen(e, user)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={-1} // We don't know the total count from the API
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* More Actions Menu */}
      <Menu
        anchorEl={moreMenuAnchorEl}
        open={isMoreMenuOpen}
        onClose={handleMoreMenuClose}
      >
        {(currentUserForMenu?.status === UserStatus.APPROVED || currentUserForMenu?.status === UserStatus.BLOCKED) && 
         currentUserForMenu?.id !== currentUser?.id && (
          <MenuItem onClick={() => handleMoreMenuAction('edit')}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            Edit Ecwid ID
          </MenuItem>
        )}
        {currentUserForMenu?.role !== UserRole.ADMIN && currentUserForMenu?.status === UserStatus.APPROVED && (
          <MenuItem onClick={() => handleMoreMenuAction('makeAdmin')}>
            <ListItemIcon>
              <AdminIcon fontSize="small" />
            </ListItemIcon>
            Make Admin
          </MenuItem>
        )}
        {currentUserForMenu?.status === UserStatus.APPROVED && 
         currentUserForMenu?.id !== currentUser?.id && (
          <MenuItem onClick={() => handleMoreMenuAction('loginAs')}>
            <ListItemIcon>
              <LockOpenIcon fontSize="small" />
            </ListItemIcon>
            Login as User
          </MenuItem>
        )}
        {currentUserForMenu && currentUserForMenu.id !== currentUser?.id && (
          <MenuItem onClick={() => handleMoreMenuAction('resetPassword')}>
            <ListItemIcon>
              <LockIcon fontSize="small" />
            </ListItemIcon>
            Reset Password
          </MenuItem>
        )}
      </Menu>

      {/* Action Confirmation Dialog */}
      <Dialog
        open={actionDialog.open}
        onClose={handleCancelAction}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {actionDialog.action === 'approve' && 'Approve User'}
          {actionDialog.action === 'block' && 'Block User'}
          {actionDialog.action === 'unblock' && 'Unblock User'}
          {actionDialog.action === 'makeAdmin' && 'Make User Admin'}
          {actionDialog.action === 'edit' && `Edit User: ${actionDialog.userName}`}
        </DialogTitle>
        <DialogContent>
          {error && actionDialog.action === 'edit' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <DialogContentText sx={{ mb: (actionDialog.action !== 'edit' && actionDialog.action !== 'approve') ? 2 : 0 }}>
            {actionDialog.action === 'approve' && `Approve ${actionDialog.userName}? You can optionally link an Ecwid Customer ID below.`}
            {actionDialog.action === 'block' && `Block ${actionDialog.userName}? They will no longer be able to log in.`}
            {actionDialog.action === 'unblock' && `Unblock ${actionDialog.userName}? They will regain access to log in.`}
            {actionDialog.action === 'makeAdmin' && `Make ${actionDialog.userName} an admin? They will have full access.`}
            {actionDialog.action === 'edit' && `Enter the Ecwid Customer ID for ${actionDialog.userName} or leave blank to clear.`}
          </DialogContentText>
          
          {(actionDialog.action === 'approve' || actionDialog.action === 'edit') && (
            <TextField
              autoFocus={actionDialog.action === 'edit'}
              margin="dense"
              label={`Ecwid Customer ID`}
              type="number"
              fullWidth
              variant="outlined"
              value={ecwidId}
              onChange={(e) => setEcwidId(e.target.value)}
              placeholder={actionDialog.action === 'edit' ? "Leave blank to clear" : "Optional"}
              helperText={actionDialog.action === 'approve' 
                 ? "Link this user to an existing Ecwid customer."
                 : "Enter a number or leave blank to clear."}
              sx={{ mt: 2 }}
              error={!!error && actionDialog.action === 'edit'}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAction} disabled={loading && actionDialog.action === 'edit'}>Cancel</Button>
          <Button 
            onClick={handleConfirmAction} 
            color={
              actionDialog.action === 'block' ? 'error' : 
              actionDialog.action === 'unblock' ? 'warning' : 
              actionDialog.action === 'approve' ? 'success' : 
              actionDialog.action === 'edit' ? 'info' :
              'primary'
            }
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? 'Processing...' : 
             actionDialog.action === 'approve' ? 'Confirm Approve' : 
             actionDialog.action === 'block' ? 'Confirm Block' : 
             actionDialog.action === 'unblock' ? 'Confirm Unblock' : 
             actionDialog.action === 'makeAdmin' ? 'Confirm Admin' :
             actionDialog.action === 'edit' ? 'Save Changes' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialog.open} onClose={handleResetPasswordClose}>
        <DialogTitle>Reset User Password</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Set a temporary password for {resetPasswordDialog.userName}. 
            The user will need to change this password after logging in.
          </DialogContentText>
          
          {resetPasswordError && (
            <Alert severity="error" sx={{ my: 2 }}>
              {resetPasswordError}
            </Alert>
          )}
          
          {resetPasswordSuccess && (
            <Alert severity="success" sx={{ my: 2 }}>
              {resetPasswordSuccess}
            </Alert>
          )}
          
          <TextField
            margin="dense"
            label="Temporary Password"
            type="text"
            fullWidth
            value={tempPassword}
            onChange={(e) => setTempPassword(e.target.value)}
            disabled={resetPasswordLoading || !!resetPasswordSuccess}
            helperText="Password must be at least 8 characters long"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResetPasswordClose} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleResetPasswordSubmit} 
            color="primary" 
            disabled={resetPasswordLoading || !!resetPasswordSuccess}
            startIcon={resetPasswordLoading ? <CircularProgress size={20} /> : null}
          >
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UsersPage; 